import React, { FC, useState, useEffect, useRef, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { Formik, Form, Field, ErrorMessage, FormikHelpers } from "formik";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Toaster, toast } from "react-hot-toast";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import AuthLayout from "components/AuthLayout/AuthLayout";
import { useAppDispatch, useAppSelector } from "store/hooks/hooks";
import { selectAuthLoading } from "store/slices/cognitoAuthSlice";
import { validatePassword } from "utils/passwordValidator";
import LucideIcon from "utils/LucideIcon";
import {
  COMPANY_NAME_PATTERN,
  EMAIL_PATTERN,
  NAME_PATTERN,
} from "utils/validationRegex";
import { authPageMeta } from "data/authPages.enum";
import { ISignupPayload } from "Interfaces/interface.auth";
import { signupUser, socialLogin } from "store/slices/authSlice";
import { environment } from '../../environments/environment';

declare global {
  interface Window {
    google: any;
  }
}

const validateForm = (values: ISignupPayload) => {
  const errors: Partial<ISignupPayload> = {};

  if (!values.companyName) {
    errors.companyName = "Company Name is required";
  } else if (values.companyName.length < 2 || values.companyName.length > 255) {
    errors.companyName = "Company Name must be between 2 and 255 characters";
  } else if (!COMPANY_NAME_PATTERN.test(values.companyName)) {
    errors.companyName =
      "Please enter a valid company name";
  }

  if (!values.name) {
    errors.name = "Name is required";
  } else if (!NAME_PATTERN.test(values.name)) {
    errors.name = "Name must contain letters only";
  }

  if (!values.email) {
    errors.email = "Email is required";
  } else if (!EMAIL_PATTERN.test(values.email)) {
    errors.email = "Please enter a valid email address";
  }

  const passwordError = validatePassword(values.password);
  if (passwordError) {
    errors.password = passwordError;
  } else if (values.password.length > 20) {
    errors.password = "Password cannot exceed 20 characters";
  }

  if (!values.confirmPassword) {
    errors.confirmPassword = "Confirm Password is required";
  } else if (values.confirmPassword !== values.password) {
    errors.confirmPassword = "Passwords must match";
  }

  return errors;
};

const PageSignUp: FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const loading = useAppSelector(selectAuthLoading);
  const [passwordType, setPasswordType] = useState("password");
  const [showConfirmPassword, setShowConfirmPassword] = useState("password");
  const googleButtonRef = useRef<HTMLDivElement>(null);

  const handleNavigate = () => {
    navigate("/signin");
  };

  const handleSubmit = async (
    values: ISignupPayload,
    { setSubmitting, setErrors }: FormikHelpers<ISignupPayload>
  ) => {
    const { email, password, name, companyName } = values;
    const errors = validateForm(values);
    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }

    try {
      await dispatch(
        signupUser({
          email,
          password,
          name,
          companyName,
        })
      ).unwrap();
      toast.success("Account created! Verification code sent to your email.");
      navigate("/verify-email", { state: { email } });
    } catch (err: any) {
      console.error("Signup error:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleCallback = useCallback(
    async (response: any) => {
      try {
        if (response.credential) {
          const result = await dispatch(
            socialLogin({ accesstoken: response.credential })
          ).unwrap();
          toast.success("Signed up with Google successfully!");
          navigate("/signin", {
            state: { email: result.user.emailAddress },
          });
        } else {
          throw new Error("No credential received");
        }
      } catch (err: any) {
        console.error("Google signup error:", err);
        toast.error(err.message || "Google signup failed. Please try again.");
      }
    },
    [dispatch, navigate]
  );

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    const initializeGoogleSignIn = () => {
      if (window.google?.accounts?.id && googleButtonRef.current) {
        if (!environment.GOOGLE_CLIENT_ID) {
          console.error("Google Client ID is not defined in .env");
          return;
        }

        window.google.accounts.id.initialize({
          client_id: environment.GOOGLE_CLIENT_ID,
          callback: handleGoogleCallback,
          itp_support: true,
          auto_select: true,
          cancel_on_tap_outside: false,
        });

        try {
          window.google.accounts.id.renderButton(googleButtonRef.current, {
            theme: "outline",
            size: "large",
            type: "icon",
            text: "signup_with",
            width: "240px",
          });
        } catch (error) {
          console.error("Failed to render Google Sign-In button:", error);
        }

        window.google.accounts.id.prompt((notification: any) => {
          console.log("Google prompt notification:", notification);
        });

        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      } else {
        console.warn("Google Identity Services or button ref not ready");
      }
    };

    const timer = setTimeout(() => {
      intervalId = setInterval(initializeGoogleSignIn, 500);
    }, 100);

    return () => {
      if (intervalId) clearInterval(intervalId);
      clearTimeout(timer);
    };
  }, [handleGoogleCallback]);

  const handleValidations = (
    isValid: boolean,
    errors: boolean,
    values: any
  ) => {
    const requiredFields = [
      "name",
      "email",
      "password",
      "confirmPassword",
      "companyName",
    ];

    const allRequiredEmpty = requiredFields.some((field) => {
      const value = values[field];
      return (
        value === null ||
        value === undefined ||
        (typeof value === "string" && value.trim() === "")
      );
    });

    if (allRequiredEmpty) {
      toast.error("Please complete all required fields to continue.");
      return;
    }

    if (!isValid) {
      const firstError = Object.values(errors).find(
        (err) => typeof err === "string"
      );
      toast.error(
        firstError || "Some fields contain errors. Please correct them."
      );
      return;
    }
  };

  return (
    <>
      <Helmet>
        <title>
          Sign Up - Start Building Your Corporate Gift Catalog | 360CUSTOMIZER
        </title>
        <meta
          name="description"
          content="Join 360CUSTOMIZER to create custom corporate gift catalogs, access AI-powered recommendations, generate branded mockups, and track quotes and orders."
        />
        <meta
          name="keywords"
          content="corporate gifts, custom products, 360CUSTOMIZER, signup, personalized gifts"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://360customizer/signup" />
      </Helmet>

      <AuthLayout {...authPageMeta.signup}>
        <motion.div
          className="w-full flex justify-center items-center"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 2, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" as const, delay: 0.2 }}
        >
          <div className="bg-white dark:bg-neutral-800 rounded-2xl w-full max-w-md p-6">
            <h2 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100 mb-4 text-center font-display">
              Create Your Account
            </h2>
            <Formik
              initialValues={{
                companyName: "",
                name: "",
                email: "",
                password: "",
                confirmPassword: "",
              }}
              validate={validateForm}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting, values, isValid, dirty }) => (
                <Form className="space-y-2">
                  <div>
                    <label className="block text-neutral-800 dark:text-neutral-200 font-medium font-body text-xs">
                      Company Name *
                    </label>
                    <Field
                      type="text"
                      name="companyName"
                      placeholder="Your company name"
                      className="mt-1 block w-full rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-sm font-normal h-10 px-3 py-2 focus:ring-2 focus:ring-primary-300 focus:border-primary-300 dark:focus:ring-primary-600 transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={isSubmitting || loading}
                    />
                    <ErrorMessage
                      name="companyName"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>

                  <div>
                    <div>
                      <label className="block text-neutral-800 dark:text-neutral-200 font-medium font-body text-xs">
                        Name *
                      </label>
                      <Field
                        type="text"
                        name="name"
                        placeholder="Your Name"
                        className="mt-1 block w-full rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-sm font-normal h-10 px-3 py-2 focus:ring-2 focus:ring-primary-300 focus:border-primary-300 dark:focus:ring-primary-600 transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-50"
                        disabled={isSubmitting || loading}
                      />
                      <ErrorMessage
                        name="name"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-neutral-800 dark:text-neutral-200 font-medium font-body text-xs">
                      Email Address *
                    </label>
                    <Field
                      type="email"
                      name="email"
                      placeholder="name@company.com"
                      className="mt-1 w-full block rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-sm font-normal h-10 px-3 py-2 focus:ring-2 focus:ring-primary-300 focus:border-primary-300 dark:focus:ring-primary-600 transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={isSubmitting || loading}
                    />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-neutral-800 dark:text-neutral-200 font-medium font-body text-xs">
                        Password
                      </label>
                      <div className="relative">
                        <Field
                          type={passwordType}
                          name="password"
                          placeholder="Password"
                          className="mt-1 block w-full rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-sm font-normal h-10 px-3 py-2 focus:ring-2 focus:ring-primary-300 focus:border-primary-300 dark:focus:ring-primary-600 transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-50"
                          disabled={isSubmitting || loading}
                        />
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-primary-500 hover:text-primary-600 text-xs font-body bg-white">
                          {passwordType === "password" && (
                            <LucideIcon
                              name="EyeOff"
                              className="p-1 cursor-pointer"
                              onClick={() => setPasswordType("text")}
                            />
                          )}
                          {passwordType === "text" && (
                            <LucideIcon
                              name="Eye"
                              className="p-1 cursor-pointer"
                              onClick={() => setPasswordType("password")}
                            />
                          )}
                        </div>
                      </div>
                      <ErrorMessage
                        name="password"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>
                    <div>
                      <label className="block text-neutral-800 dark:text-neutral-200 font-medium font-body text-xs">
                        Confirm Password
                      </label>
                      <div className="relative">
                        <Field
                          type={showConfirmPassword}
                          name="confirmPassword"
                          placeholder="Confirm Password"
                          className="mt-1 block w-full rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-sm font-normal h-10 px-3 py-2 focus:ring-2 focus:ring-primary-300 focus:border-primary-300 dark:focus:ring-primary-600 transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-50"
                          disabled={isSubmitting || loading}
                        />
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-primary-500 hover:text-primary-600 text-xs font-body bg-white">
                          {showConfirmPassword === "password" && (
                            <LucideIcon
                              name="EyeOff"
                              className="p-1 cursor-pointer"
                              onClick={() => setShowConfirmPassword("text")}
                            />
                          )}
                          {showConfirmPassword === "text" && (
                            <LucideIcon
                              name="Eye"
                              className="p-1 cursor-pointer"
                              onClick={() => setShowConfirmPassword("password")}
                            />
                          )}
                        </div>
                      </div>
                      <ErrorMessage
                        name="confirmPassword"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>
                  </div>

                  <motion.div
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    className="mt-4"
                  >
                    <ButtonPrimary
                      type="submit"
                      disabled={Boolean(isSubmitting) || Boolean(loading)}
                      loading={isSubmitting || loading}
                      onClick={() => handleValidations(isValid, dirty, values)}
                      className="text-white font-semibold py-2 rounded-xl shadow-md transition-colors duration-300 hover:scale-105 ease-in text-sm disabled:cursor-not-allowed disabled:text-gray-800"
                      sizeClass="w-full"
                    >
                      {(!isSubmitting || !loading) && "Create Account"}
                    </ButtonPrimary>
                  </motion.div>

                  <div className="mt-3">
                    <div className="relative text-center">
                      <span className="relative z-10 inline-block px-3 font-medium text-xs bg-white dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400">
                        Or sign up with
                      </span>
                      <div className="absolute left-0 w-full top-1/2 transform -translate-y-1/2 border border-neutral-200 dark:border-neutral-700"></div>
                    </div>
                    <div className="flex justify-center gap-3 mt-3">
                      <div
                        id="google-button"
                        ref={googleButtonRef}
                        className={`google-signin-button min-w-[240px] min-h-[40px] flex items-center justify-center 
          ${isSubmitting ? "pointer-events-none opacity-50" : ""}`}
                      >
                        <span className="text-neutral-500">
                          Loading Google Sign-In...
                        </span>
                      </div>{" "}
                    </div>
                  </div>

                  <div className="mt-3 text-center">
                    <span className="text-neutral-700 dark:text-neutral-300 font-body text-sm">
                      Already have an account?{" "}
                      <span
                        onClick={() => {
                          if (!isSubmitting) handleNavigate();
                        }}
                        className={`text-primary-500 hover:text-primary-600 font-medium hover:underline transition-colors duration-300 cursor-pointer
              ${isSubmitting ? "pointer-events-none opacity-50" : ""}`}
                      >
                        Sign in
                      </span>
                    </span>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </motion.div>
        <Toaster />
      </AuthLayout>
    </>
  );
};

export default PageSignUp;
